/**
 * 帳號管理頁面（僅 admin）。
 * 對應原生版 app.js viewUsers + openUserForm。
 *
 * API：
 *   GET    /api/v1/users              — 帳號清單
 *   POST   /api/v1/users              — 新增帳號
 *   PATCH  /api/v1/users/{id}         — 編輯（含停用 disabled: true）
 *   DELETE /api/v1/users/{id}?purge=true — 永久刪除（後端 409 保護）
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtTime } from '@/lib/format';
import { order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/DataTable';
import { Drawer } from '@/components/Drawer';
import type { Site, Role, UserListItem } from '@/types';

/* ── 角色標籤與提示文案（與側欄 AppShell 的 roleLabel 用同一組名稱） ── */
const ROLE_LABEL: Record<Role, string> = {
  admin: '系統管理員',
  researcher: '研究人員',
  viewer: '唯讀使用者',
};
const ROLE_HINT: Record<Role, string> = {
  admin: '可管理帳號、場域、裝置、部署，並存取所有資料。',
  researcher: '可瀏覽事件、建立資料集與匯出，但不可管理帳號與場域。',
  viewer: '只能觀看儀表板與事件，不可異動任何設定。',
};

export default function UsersPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();

  /* ── 資料查詢 ── */
  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => apiGet<UserListItem[]>('/api/v1/users'),
  });
  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiGet<Site[]>('/api/v1/sites'),
  });

  const users = usersQ.data ?? [];
  const sites = sitesQ.data ?? [];

  /* ── 抽屜狀態 ── */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<UserListItem | null>(null);

  /* ── 表單欄位 ── */
  const [fUsername, setFUsername] = useState('');
  const [fDisplayName, setFDisplayName] = useState('');
  const [fPassword, setFPassword] = useState('');
  const [fRole, setFRole] = useState<Role>('viewer');
  const [fSiteCodes, setFSiteCodes] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState('');

  const isNew = editing === null && drawerOpen;

  const openCreate = () => {
    setEditing(null);
    setFUsername('');
    setFDisplayName('');
    setFPassword('');
    setFRole('viewer');
    setFSiteCodes(new Set());
    setFormError('');
    setDrawerOpen(true);
  };
  const openEdit = (u: UserListItem) => {
    setEditing(u);
    setFUsername(u.username);
    setFDisplayName(u.display_name ?? '');
    setFPassword('');
    setFRole(u.role);
    setFSiteCodes(new Set(u.sites ?? []));
    setFormError('');
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);

  /* ── 角色切換時同步提示 ── */
  const isAdmin = fRole === 'admin';

  /* 重設場域勾選：切到 admin 時不需個別指派 */
  useEffect(() => {
    // 切成 admin 不清空，只是畫面 disabled
  }, [fRole]);

  /* ── 新增帳號 ── */
  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<UserListItem>('/api/v1/users', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      closeDrawer();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  /* ── 編輯帳號 ── */
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      apiPatch<UserListItem>(`/api/v1/users/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      closeDrawer();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  /* ── 停用 / 啟用 ── */
  const toggleMut = useMutation({
    mutationFn: ({ id, disabled }: { id: number; disabled: boolean }) =>
      apiPatch<UserListItem>(`/api/v1/users/${id}`, { disabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (e: Error) => alert(e.message),
  });

  /* ── 永久刪除 ── */
  const deleteMut = useMutation({
    mutationFn: (id: number) =>
      apiDelete(`/api/v1/users/${id}?purge=true`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (e: Error) => alert(e.message),
  });

  const handleDelete = (u: UserListItem) => {
    if (!window.confirm(`永久刪除帳號「${u.username}」？此動作無法復原。`)) return;
    deleteMut.mutate(u.id);
  };

  const handleToggle = (u: UserListItem) => {
    toggleMut.mutate({ id: u.id, disabled: !u.disabled });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    const siteCodes = [...fSiteCodes];
    if (isNew) {
      createMut.mutate({
        username: fUsername,
        password: fPassword,
        role: fRole,
        display_name: fDisplayName,
        site_codes: siteCodes,
      });
    } else if (editing) {
      const body: Record<string, unknown> = {
        role: fRole,
        display_name: fDisplayName,
        site_codes: siteCodes,
      };
      if (fPassword) body.password = fPassword;
      patchMut.mutate({ id: editing.id, body });
    }
  };

  const submitting = createMut.isPending || patchMut.isPending;

  const toggleSite = (code: string) => {
    setFSiteCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  /* ── 表格欄位 ── */
  const columns: Column<UserListItem>[] = [
    {
      key: 'username',
      header: '帳號',
      cell: (u) => (
        <div>
          <div className="font-semibold">{u.display_name || u.username}</div>
          <div className="font-mono text-[11px] text-muted-foreground">{u.username}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: '角色',
      cell: (u) => (
        <Badge variant={u.role === 'admin' ? 'info' : 'muted'}>
          {ROLE_LABEL[u.role] ?? u.role}
        </Badge>
      ),
    },
    {
      key: 'sites',
      header: '可見場域',
      cell: (u) => (
        <span className="text-muted-foreground text-[12px]">
          {u.role === 'admin' ? '全部場域' : (u.sites.length ? u.sites.join('、') : '未指派')}
        </span>
      ),
    },
    {
      key: 'status',
      header: '狀態',
      cell: (u) => (
        <Badge variant={u.disabled ? 'muted' : 'success'}>
          {u.disabled ? '已停用' : '啟用中'}
        </Badge>
      ),
    },
    {
      key: 'last_login',
      header: '最後登入',
      cell: (u) => (
        <span className="font-mono text-[11.5px] text-muted-foreground">
          {u.last_login_at ? fmtTime(u.last_login_at) : '從未登入'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (u) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => openEdit(u)}>
            編輯
          </Button>
          {me && u.username !== me.username && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleToggle(u)}
                disabled={toggleMut.isPending}
              >
                {u.disabled ? '啟用' : '停用'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                title="永久刪除；已有操作紀錄的帳號只能停用"
                onClick={() => handleDelete(u)}
                disabled={deleteMut.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
                <span>刪除</span>
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Card className="reveal admin-data-card overflow-hidden" style={order(0)}>
        <CardHeader className="pb-5">
          <div>
            <CardTitle>平台帳號（{users.length}）</CardTitle>
            <CardDescription>共 {users.length} 個帳號；停用後既有登入立即失效</CardDescription>
          </div>
          <Button onClick={openCreate}>
            <ShieldCheck className="h-[15px] w-[15px]" />
            <span>新增帳號</span>
          </Button>
        </CardHeader>
        <DataTable<UserListItem>
          rows={users}
          columns={columns}
          rowKey={(u) => String(u.id)}
          loading={usersQ.isLoading}
          error={usersQ.error}
          empty="尚無帳號。"
        />
      </Card>

      {/* 新增 / 編輯 Drawer */}
      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={isNew ? '新增帳號' : `編輯 ${editing?.username ?? ''}`}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* 帳號 */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">帳號</span>
            <Input
              value={fUsername}
              onChange={(e) => setFUsername(e.target.value)}
              disabled={!isNew}
              required
              autoComplete="off"
            />
            {!isNew && (
              <span className="text-[11.5px] text-muted-foreground">帳號建立後不可更改</span>
            )}
          </label>

          {/* 顯示名稱 */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">顯示名稱</span>
            <Input
              value={fDisplayName}
              onChange={(e) => setFDisplayName(e.target.value)}
              placeholder="例如：王小明"
            />
          </label>

          {/* 密碼 */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">
              {isNew ? '密碼' : '重設密碼（留空則不改）'}
            </span>
            <Input
              type="password"
              value={fPassword}
              onChange={(e) => setFPassword(e.target.value)}
              required={isNew}
              placeholder="至少 8 個字元"
              autoComplete="new-password"
            />
            {!isNew && (
              <span className="text-[11.5px] text-muted-foreground">
                改密碼會讓該帳號其他裝置的登入立即失效
              </span>
            )}
          </label>

          {/* 角色 */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">角色</span>
            <select
              className="flex min-h-[44px] w-full rounded-sm border border-border bg-card px-3.5 py-2 text-sm"
              value={fRole}
              onChange={(e) => setFRole(e.target.value as Role)}
            >
              {(Object.entries(ROLE_LABEL) as [Role, string][]).map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
            <span className="text-[11.5px] text-muted-foreground">{ROLE_HINT[fRole]}</span>
          </label>

          {/* 可見場域 */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">可見場域</span>
            <div className={`flex flex-col gap-2 ${isAdmin ? 'opacity-45' : ''}`}>
              {sites.map((s) => (
                <label key={s.code} className="flex min-h-11 items-center gap-3 rounded-sm border border-border px-3 py-2">
                  <input
                    type="checkbox"
                    className="h-5 w-5 shrink-0 accent-primary"
                    checked={fSiteCodes.has(s.code)}
                    disabled={isAdmin}
                    onChange={() => toggleSite(s.code)}
                  />
                  <span>{s.code}　{s.name}</span>
                </label>
              ))}
            </div>
            <span className="text-[11.5px] text-muted-foreground">
              {isAdmin
                ? '系統管理員一律可見全部場域，不需個別指派。'
                : '未勾選任何場域時，該帳號看不到任何裝置與事件。'}
            </span>
          </div>

          {/* 錯誤訊息 */}
          {formError && (
            <p className="text-[13px] text-destructive">{formError}</p>
          )}

          <Button type="submit" disabled={submitting} block>
            {submitting ? '處理中…' : isNew ? '建立帳號' : '儲存變更'}
          </Button>
        </form>
      </Drawer>
    </>
  );
}
